<h2 mat-dialog-title>Risk item</h2>

<mat-dialog-content class="risk-detail">

    <div class="detail-block">
        <div class="detail-label">Issue</div>
        <div class="detail-value issue">{{ item.issue }}</div>
    </div>

    <!-- D8 — THE VERBATIM QUOTE. Evidence, never editable. Before a doctor puts an allegation on
         their own letterhead they must see the sentence they actually said. -->
    <div class="detail-block" *ngIf="item.quote">
        <div class="detail-label">What was said</div>
        <blockquote class="detail-value quote">“{{ item.quote }}”</blockquote>
        <div class="detail-hint">Recorded verbatim from the dictation and never edited.</div>
    </div>

    <div class="detail-grid">
        <div class="detail-block">
            <div class="detail-label">Category</div>
            <div class="detail-value">{{ categoryLabel(item.category) }}</div>
        </div>
        <div class="detail-block">
            <div class="detail-label">Severity</div>
            <div class="detail-value">
                <span class="severity-indicator" [ngClass]="item.severity"></span>
                {{ severityLabel(item.severity) }}
            </div>
        </div>
        <div class="detail-block">
            <div class="detail-label">Date of issue</div>
            <div class="detail-value">{{ item.dateOfIssue | date : 'dd/MM/yyyy' }}</div>
        </div>
        <div class="detail-block">
            <div class="detail-label">Status</div>
            <div class="detail-value">
                <span class="status-badge" [ngClass]="item.status?.toLowerCase()">{{ item.status }}</span>
            </div>
        </div>
    </div>

    <!-- §28 / P5-P6 — PHOTOGRAPHS.

         OUTSIDE the `showExtendedDetail` gate below, deliberately. That flag hides Facility,
         Patient, Notes and the provenance footer at the user's 2026-08-31 request and stays
         `false`; putting the gallery inside it would make D15's "media attaches on every surface"
         silently untrue on this one, and the doctor would see an upload control on the routed page
         and none here with no way to tell why.

         This is the ONE surface reached by a row with NO patient (§5c) — "pharmacy had no
         ceftriaxone" — which is exactly the kind of issue a photograph of an empty shelf evidences,
         so it is the last surface that should have been left text-only. -->
    <div class="detail-block">
        <div class="detail-label">Photographs</div>
        <app-file-upload [fileObjects]="loadedMediaObjects" [allowMultiple]="true"
            [allowedFileTypes]="'image/*, application/pdf'" title=""
            hintMessage="Attach a photo of the equipment, consumable or facility issue."
            (fileUpload)="onRiskMediaUpload($event)" (fileDelete)="onRiskMediaDelete($event)"
            (filesEdited)="onRiskMediaEdited($event)" [cameraEnabled]="true" [enableEditing]="true">
        </app-file-upload>
        <!-- Explicit, because this dialog saves one field per action rather than on a form submit.
             Without it a doctor could attach a photo, close the dialog and lose it silently. -->
        <button mat-stroked-button color="primary" (click)="saveMedia()"
            [disabled]="!hasMediaChange || saving" class="save-media-button">
            Save photographs
        </button>
        <!-- The `Reported` divergence, stated where it costs nothing. Attaching after reporting is
             ALLOWED (user decision, 2026-09-01: "leave it up to the user, same as the facility
             edit") — this is not a warning and there is no gate, it is simply the honest fact that
             the document the hospital holds predates these photographs. -->
        <div class="detail-hint" *ngIf="photosAddedAfterReporting > 0">
            This issue was reported on {{ item.reportedAt | date : 'dd/MM/yyyy' }}.
            {{ photosAddedAfterReporting }}
            {{ photosAddedAfterReporting === 1 ? 'photograph was' : 'photographs were' }}
            attached afterwards and {{ photosAddedAfterReporting === 1 ? 'is' : 'are' }} not in the
            document that was sent.
        </div>
    </div>

    <!-- §4 / D5 / D6 — the facility SNAPSHOT, resolved server-side once at confirmation and never
         re-resolved. Read-only by design: a live join would re-file a past issue under whichever
         hospital the patient happens to be in now. -->
    <!--
      HIDDEN FOR NOW at the user's request (2026-08-31): Facility, Patient, Notes and the
      "Added by / Reported" footer.

      Gated rather than deleted, because "for now" is what was asked. Flipping
      `showExtendedDetail` to true in the component restores all four exactly as they were -
      no markup to reconstruct, no reasoning to rediscover.

      NOTHING IS LOST BY HIDING THIS. Notes already saved stay in the row and still export;
      they are simply not shown or editable here. The facility snapshot (§4) and the patient
      link are still stored, still filtered on, and still on the PDF.
    -->
    <ng-container *ngIf="showExtendedDetail">
        <div class="detail-block">
            <div class="detail-label">Facility</div>
            <div class="detail-value">{{ facilityLabel() }}</div>
            <div class="detail-value sub" *ngIf="item.wardName">Ward: {{ item.wardName }}</div>
            <div class="detail-hint">{{ resolutionLabel() }}</div>
            <div class="detail-hint" *ngIf="!item.hospitalId && item.hospitalName">
                Recorded from the admission history; the name did not match a hospital record in this practice, so it is
                filed under the name as written.
            </div>
        </div>

        <div class="detail-block">
            <div class="detail-label">Patient</div>
            <div class="detail-value">
                {{ patientLabel() }}
                <span class="patient-number" *ngIf="item.Patient?.patientNumber">({{ item.Patient.patientNumber }})</span>
            </div>
            <button mat-stroked-button *ngIf="item.patientId" (click)="openPatientFile()" class="patient-file-button">
                <mat-icon>visibility</mat-icon>
                Open patient file
            </button>
        </div>

        <div class="detail-block">
            <div class="detail-label">Notes</div>
            <mat-form-field appearance="outline" class="notes-field">
                <textarea matInput rows="3" [(ngModel)]="notes" placeholder="Internal notes about this issue"></textarea>
            </mat-form-field>
            <button mat-stroked-button color="primary" (click)="saveNotes()" [disabled]="!hasNotesChange || saving">
                Save notes
            </button>
        </div>

        <div class="detail-block meta">
            <span *ngIf="item.createdBy">Added by {{ item.createdBy }}</span>
            <span *ngIf="item.reportedAt">· Reported {{ item.reportedAt | date : 'dd/MM/yyyy' }}</span>
        </div>
    </ng-container>

</mat-dialog-content>

<mat-dialog-actions class="risk-detail-actions">
    <!-- G5 / D9 — the same transitions as the register list, from the detail view. -->
    <button mat-stroked-button *ngFor="let status of statusOptions" [disabled]="item.status === status || saving"
        (click)="setStatus(status)">
        {{ status }}
    </button>
    <span class="spacer"></span>
    <button mat-button (click)="close()">Close</button>
</mat-dialog-actions>
